// frame: ndI5h @ ui/dogdisk-user.pen 弹框-排序筛选
// 触发:UUYuk(file-browser)/ BYeJZ(folder-detail)sectionHeader 排序按钮。
// 受控组件:sortBy + order 由父组件维护,本 sheet 仅 render + onChange。
import type { ReactNode } from 'react'
import type { SortBy, SortOrder, SortState } from '../../_lib/file-sort'

export type SortFilterSheetProps = {
  open: boolean
  onClose: () => void
  value: SortState
  onChange: (next: SortState) => void
  /** 可选:限制展示哪些排序维度;缺省=全部(不影响既有调用方) */
  options?: SortBy[]
}

const I = (paths: ReactNode) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths}</svg>
)

const SORT_OPTIONS: { key: SortBy; label: string; icon: ReactNode }[] = [
  { key: 'created_at', label: '按时间排序', icon: I(<><path d="M8 2v4" /><path d="M16 2v4" /><rect width="18" height="18" x="3" y="4" rx="2" /><path d="M3 10h18" /></>) },
  { key: 'name', label: '按名称排序', icon: I(<><polyline points="4 7 4 4 20 4 20 7" /><line x1="9" x2="15" y1="20" y2="20" /><line x1="12" x2="12" y1="4" y2="20" /></>) },
  { key: 'size', label: '按大小排序', icon: I(<><line x1="22" x2="2" y1="12" y2="12" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /><line x1="6" x2="6.01" y1="16" y2="16" /><line x1="10" x2="10.01" y1="16" y2="16" /></>) },
  { key: 'type', label: '按类型排序', icon: I(<><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" /><polyline points="14 2 14 8 20 8" /></>) },
]

const ORDER_OPTIONS: { key: SortOrder; label: string; icon: ReactNode }[] = [
  { key: 'asc', label: '升序', icon: I(<><path d="m5 12 7-7 7 7" /><path d="M12 19V5" /></>) },
  { key: 'desc', label: '降序', icon: I(<><path d="M12 5v14" /><path d="m19 12-7 7-7-7" /></>) },
]

const CHECK = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
)

function OptionRow({ active, testid, label, icon, onClick }: {
  active: boolean
  testid: string
  label: string
  icon: ReactNode
  onClick: () => void
}) {
  return (
    <li>
      <button
        type="button"
        data-testid={testid}
        onClick={onClick}
        className={
          'flex h-14 w-full items-center justify-between px-6 text-left active:bg-[#F6F7F8] ' +
          (active ? 'text-[#19caad]' : 'text-[#1A1A1A]')
        }
      >
        <span className="flex items-center gap-4">
          {icon}
          <span className={'text-[19px] ' + (active ? 'font-semibold' : '')}>{label}</span>
        </span>
        {active && CHECK}
      </button>
    </li>
  )
}

export function SortFilterSheet({ open, onClose, value, onChange, options }: SortFilterSheetProps) {
  if (!open) return null

  return (
    <div
      data-frame-id="ndI5h"
      data-testid="ndI5h"
      role="dialog"
      aria-modal="true"
      aria-label="排序筛选"
      className="fixed inset-0 z-50 flex items-end bg-black/50"
      onClick={onClose}
    >
      <div
        className="mx-auto w-full max-w-[402px] rounded-t-3xl border border-[#E0E0E0] bg-white pt-2"
        style={{ paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex h-6 items-center justify-center">
          <span className="h-1 w-10 rounded-full bg-[#E5E7EB]" />
        </div>
        <div className="px-6 py-4">
          <h2 className="text-[22px] font-bold text-[#1A1A1A]" style={{ fontFamily: 'Bricolage Grotesque, ui-sans-serif, system-ui' }}>
            排序方式
          </h2>
        </div>
        <ul className="flex flex-col">
          {SORT_OPTIONS.filter((o) => !options || options.includes(o.key)).map((opt) => (
            <OptionRow
              key={opt.key}
              active={opt.key === value.sortBy}
              testid={`ndI5h-sort-${opt.key}`}
              label={opt.label}
              icon={opt.icon}
              onClick={() => onChange({ ...value, sortBy: opt.key })}
            />
          ))}
        </ul>
        <div className="h-2 bg-[#F6F7F8]" />
        <div className="px-6 pt-4">
          <h3 className="text-[14px] font-semibold text-[#9CA3AF]">排列顺序</h3>
        </div>
        <ul className="flex flex-col pb-2">
          {ORDER_OPTIONS.map((opt) => (
            <OptionRow
              key={opt.key}
              active={opt.key === value.order}
              testid={`ndI5h-order-${opt.key}`}
              label={opt.label}
              icon={opt.icon}
              onClick={() => onChange({ ...value, order: opt.key })}
            />
          ))}
        </ul>
      </div>
    </div>
  )
}
